import { Actions } from '../../components/Actions/Actions';
import { Box } from '../../components/Box/Box';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { Chips } from '../../components/Chips/Chips';
import { ContentContainer } from '../../components/ContentContainer/ContentContainer';
import { DescriptionText } from '../../components/DescriptionText/DescriptionText';
import { Layout } from '../../components/Layout/Layout';
import { Logo } from '../../components/Logo/Logo';
import { Paginate } from '../../components/Paginate/Paginate';
import { Panel } from '../../components/Panel/Panel';
import { StatusMessage } from '../../components/StatusMessage/StatusMessage';
import { TruncateText } from '../../components/Text/subcomponents/TruncateText';
import { Text } from '../../components/Text/Text';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import { heights } from '../../tokens/sizes/heights';
import { SharedPageLayoutProps } from '../../types/SharedPageLayoutProps';

// TODO EKP-16459: this explicitly indicates throughout the implementation the usage of negative margins.  The reason is because `PrivateRoute` unfortunately sets opinionated spacings above the `PageLayout`.  Once all pages are formally using `PageLayout`, we can refactor this away from `PrivateRoute`.
const DEPRECATED_mx = 8;
const FOOTER_HEIGHT = `${heights['footer.height']}px`;

export const PageLayout = ({
  actions = [],
  children = null,
  chips,
  description,
  footerActions,
  header: headerContents,
  headerCalloutContent,
  height,
  hideHeaderDivider = false,
  loadingContent,
  moreActions,
  nav,
  panel,
  placeholderContent,
  statusMessage,
  title,
  subtitle,
  welcome,
}: SharedPageLayoutProps) => {
  useDisableBodyScroll(Boolean(height));

  const hasActions = actions.length > 0 || Boolean(moreActions);
  const hasFooterActions = Boolean(footerActions?.length);

  const textAlign = welcome ? 'center' : 'left';
  const shouldConstrainFooterLayout = hasFooterActions && !height;
  const shouldFixFooter = Boolean(footerActions?.length && height);

  const pageTitle = (
    <TruncateText as="h1" m={0} textAlign={textAlign} variant="title">
      {title}
    </TruncateText>
  );

  const pageDescription = description && (
    <DescriptionText text={description} textAlign={textAlign} />
  );

  const pageSubtitle = subtitle && (
    <Text align="center" variant="body-bold">
      {subtitle}
    </Text>
  );
  const pageNav = nav && (
    <Layout
      align="center"
      alignSelf="flex-start"
      justify="space-between"
      ml={-4}
      spacing={4}
      w="100%"
    >
      <Breadcrumbs actions={nav.actions} />
      {nav.paginate && (
        <Paginate {...nav.paginate} mode="count" name="page nav paginate" />
      )}
    </Layout>
  );

  const contents = (
    <Layout
      flex="auto"
      minH={0}
      spacing={8}
      style={hasFooterActions ? { overflowY: 'auto' } : undefined}
    >
      <Layout
        direction="column"
        flex="auto"
        py={4}
        minW={0}
        spacing={4}
        style={shouldFixFooter ? { paddingBottom: FOOTER_HEIGHT } : undefined}
      >
        {statusMessage && <StatusMessage {...statusMessage} />}
        <ContentContainer
          loadingContent={loadingContent}
          placeholderContent={placeholderContent}
        >
          <>{children}</>
        </ContentContainer>
      </Layout>
    </Layout>
  );

  const pageActions = <Actions actions={actions} />;

  const footer = footerActions && (
    <Box
      as="footer"
      mx={shouldFixFooter ? undefined : -DEPRECATED_mx}
      styles={
        shouldFixFooter
          ? { ...componentStyles.footer, ...componentStyles.fixedFooter }
          : componentStyles.footer
      }
    >
      <Actions actions={footerActions} />
    </Box>
  );

  if (welcome) {
    const { onboardingIds, content, enableLogo } = welcome;
    return (
      <Layout
        as="header"
        align="center"
        direction="column"
        h={height}
        px={DEPRECATED_mx}
        spacing={12}
      >
        {enableLogo && <Logo />}
        {pageNav}
        <Layout direction="column" py={4} spacing={4}>
          {pageTitle}
          {pageSubtitle}
          {pageDescription}
        </Layout>
        <ContentContainer loadingContent={loadingContent}>
          <>
            {content}
            {onboardingIds && (
              <Layout preset="cards">
                {onboardingIds.map((onboardingId) => (
                  <Box id={onboardingId} p={4} minW={300} minH={360} />
                ))}
              </Layout>
            )}
            {pageActions}
          </>
        </ContentContainer>
        {footer}
      </Layout>
    );
  }

  const isLoading = loadingContent?.isLoading;

  const header = (
    <Layout
      as="header"
      borderBottom={hideHeaderDivider ? undefined : 'border.divider'}
      direction="column"
      mx={-DEPRECATED_mx}
      pb={3}
      pt={nav ? undefined : 2}
      px={DEPRECATED_mx}
      spacing={3}
    >
      {pageNav}
      <Layout align="center" justify="space-between" spacing={4}>
        <Layout align="center" spacing={4}>
          {pageTitle}
          {chips && <Chips chips={chips} />}
        </Layout>
        <Layout align="center" spacing={2}>
          {headerCalloutContent}
          {hasActions && (
            <Actions
              actions={actions.map((action) => ({
                ...action,
                disabled: isLoading || action.disabled,
              }))}
              moreActions={moreActions?.map((action) => ({
                ...action,
                disabled: isLoading || action.disabled,
              }))}
            />
          )}
        </Layout>
      </Layout>
      {pageDescription}
      {headerContents}
    </Layout>
  );

  return (
    <Layout
      h={height}
      minH="100%"
      mx={-DEPRECATED_mx}
      position="relative"
      px={DEPRECATED_mx}
      spacing={3}
      style={shouldConstrainFooterLayout ? { height: '100%' } : undefined}
    >
      <Layout direction="column" h="100%" w="100%">
        {header}
        {contents}
        {footer}
      </Layout>
      {panel && <Panel position="right" {...panel} />}
    </Layout>
  );
};

const componentStyles = {
  footer: {
    backgroundColor: 'background',
    borderTop: 'border.divider',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'flex-end',
    minHeight: FOOTER_HEIGHT,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 2,
  },
  fixedFooter: {
    bottom: 0,
    left: 0,
    position: 'fixed',
    right: 0,
  },
};
